
// UAE Emirates constants for consistent usage
export const UAE_EMIRATES = [
  'Abu Dhabi',
  'Dubai', 
  'Sharjah',
  'Ajman',
  'Umm Al Quwain',
  'Ras Al Khaimah',
  'Fujairah'
];

/**
 * Contact Message structure
 * @typedef {Object} ContactMessage
 * @property {string} name - User's full name
 * @property {string} emirate - UAE Emirate/State selection
 * @property {string} email - User's email address
 * @property {string} phoneNumber - User's phone number (UAE format preferred)
 * @property {string} message - User's message/query
 * @property {Date} [timestamp] - Optional: Timestamp when the message was submitted
 * @property {'new' | 'read' | 'replied' | 'resolved'} [status] - Optional: Status of the message
 */

// Export the type definition for documentation
export const ContactMessageType = {
  name: 'string',
  emirate: 'string',
  email: 'string',
  phoneNumber: 'string',
  message: 'string',
  timestamp: 'Date (optional)',
  status: 'string (optional) - new, read, replied, or resolved'
};

// Status options for contact messages
export const MESSAGE_STATUS = {
  NEW: 'new',
  READ: 'read', 
  REPLIED: 'replied',
  RESOLVED: 'resolved'
};

/**
 * Default contact message structure
 */
export const defaultContactMessage = {
  name: '',
  emirate: '',
  email: '',
  phoneNumber: '',
  message: '',
  timestamp: new Date(),
  status: 'new'
};

/**
 * Validates a contact message object
 * @param {ContactMessage} message - The contact message to validate
 * @returns {Object} validation result with isValid and errors
 */
export const validateContactMessage = (message) => {
  const errors = {};

  if (!message.name || message.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters long';
  }

  if (!message.email) {
    errors.email = 'Email is required';
  } else if (!/\S+@\S+\.\S+/.test(message.email)) {
    errors.email = 'Email is invalid';
  }

  if (!message.emirate) {
    errors.emirate = 'Please select an emirate';
  } else if (!UAE_EMIRATES.includes(message.emirate)) {
    errors.emirate = 'Please select a valid UAE emirate';
  }

  if (message.phoneNumber && !/^[\+]?[0-9\s\-\(\)]{8,20}$/.test(message.phoneNumber)) {
    errors.phoneNumber = 'Please enter a valid phone number';
  }

  if (!message.message || message.message.trim().length < 10) {
    errors.message = 'Message must be at least 10 characters long';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export default {
  UAE_EMIRATES,
  ContactMessageType,
  MESSAGE_STATUS,
  defaultContactMessage,
  validateContactMessage
};